<script setup>
// Immersive Walkthrough viewer. Immersive links are ordinary
// Nexceo::ProjectDocument rows (document_type 'immersive', or the legacy
// single-purpose 'immersive_sample_flat') with a title + external_url the
// admin manages from the existing Documents tab — no separate storage.
//
// Note on the docs/addon reference design: it shows a custom-built 360°
// pano viewer (drag-to-look-around, mini-map, rotate dial) rendering its
// own panorama image. That's a different technical foundation than what
// NEXCEO stores (an admin-pasted external_url to whatever provider —
// Matterport/Kuula/etc. — was used), and per the explicit "render in an
// iframe, never bypass X-Frame-Options/CSP" instruction, this stays an
// iframe embed of that URL — only the surrounding chrome (header, scene
// selector, fullscreen placement) follows the reference's visual hierarchy.
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import projectDocumentsAPI from 'dashboard/api/nexceo/projectDocuments';

const props = defineProps({
  // Unused here — SalesProjectShow passes it to every tab uniformly;
  // declared so Vue doesn't fall it through as a stray DOM attribute.
  project: { type: Object, default: null },
  projectId: { type: Number, required: true },
});

const IMMERSIVE_TYPES = ['immersive', 'immersive_sample_flat'];
const LOAD_TIMEOUT_MS = 7000;
// Verified against a real X-Frame-Options-refused provider during testing:
// Chrome substitutes a blank/error frame for the blocked navigation and
// still fires 'load' on it — so "did load fire" alone can't tell blocked
// apart from working. What it doesn't fake is timing: that substitution
// resolves near-instantly, while a real cross-origin page load (TLS
// handshake + server response + parse) essentially never completes this
// fast. Conservative on purpose — false positives (flagging a genuinely
// fast working embed as blocked) are worse than false negatives here, since
// "Open in a new tab" is always available regardless of this heuristic.
const FAST_LOAD_SUSPECT_MS = 300;

const documents = ref([]);
const isLoading = ref(false);
const error = ref(null);
const activeId = ref(null);
const iframeEl = ref(null);
const viewerEl = ref(null);
const frameLoaded = ref(false);
const suspectBlocked = ref(false);
const fullscreenSupported = ref(false);
const isFullscreen = ref(false);
let loadTimeoutHandle = null;
let frameLoadStartedAt = 0;

const immersiveItems = computed(() =>
  documents.value.filter(
    d =>
      IMMERSIVE_TYPES.includes(d.document_type) &&
      d.customer_shareable &&
      d.shareable_url
  )
);

const activeItem = computed(
  () =>
    immersiveItems.value.find(d => d.id === activeId.value) ??
    immersiveItems.value[0] ??
    null
);

function selectItem(doc) {
  activeId.value = doc.id;
}

// Best-effort only: a same-origin-policy iframe cannot tell the parent page
// whether the provider's X-Frame-Options/CSP frame-ancestors header refused
// the embed. Two independent signals feed suspectBlocked: the frame never
// loading at all within LOAD_TIMEOUT_MS (network failure/hang — reliable),
// and the frame loading suspiciously fast (see FAST_LOAD_SUSPECT_MS above —
// a heuristic, not a guarantee). The "Open in New Tab" action below is
// always available regardless, per the no-embedding-bypass rule.
function armLoadWatchdog() {
  frameLoaded.value = false;
  suspectBlocked.value = false;
  frameLoadStartedAt = Date.now();
  clearTimeout(loadTimeoutHandle);
  loadTimeoutHandle = setTimeout(() => {
    if (!frameLoaded.value) suspectBlocked.value = true;
  }, LOAD_TIMEOUT_MS);
}

function onFrameLoad() {
  frameLoaded.value = true;
  clearTimeout(loadTimeoutHandle);
  if (Date.now() - frameLoadStartedAt < FAST_LOAD_SUSPECT_MS) {
    suspectBlocked.value = true;
  }
}

watch(activeItem, () => nextTick(armLoadWatchdog));

async function toggleFullscreen() {
  const el = viewerEl.value;
  if (!el) return;
  try {
    if (!document.fullscreenElement) {
      await (el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.());
      isFullscreen.value = true;
    } else {
      await (document.exitFullscreen?.() ?? document.webkitExitFullscreen?.());
      isFullscreen.value = false;
    }
  } catch {
    // Fullscreen can be denied by the browser (e.g. a permissions-policy
    // restriction, or a user gesture requirement not met) — fail silently
    // rather than surface a scary error for a non-essential enhancement.
  }
}

function handleFullscreenChange() {
  isFullscreen.value = !!document.fullscreenElement;
}

async function load() {
  isLoading.value = true;
  error.value = null;
  try {
    const { data } = await projectDocumentsAPI.get(props.projectId, {
      status: 'active',
    });
    documents.value = Array.isArray(data) ? data : [];
  } catch {
    error.value = 'Failed to load immersive walkthroughs.';
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  fullscreenSupported.value = !!(
    document.fullscreenEnabled || document.webkitFullscreenEnabled
  );
  document.addEventListener('fullscreenchange', handleFullscreenChange);
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
  load();
});

onUnmounted(() => {
  clearTimeout(loadTimeoutHandle);
  document.removeEventListener('fullscreenchange', handleFullscreenChange);
  document.removeEventListener(
    'webkitfullscreenchange',
    handleFullscreenChange
  );
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex-1 flex flex-col overflow-hidden">
    <div v-if="isLoading" class="flex justify-center py-16">
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-7" />
    </div>

    <div
      v-else-if="error"
      class="text-sm text-red-600 dark:text-red-400 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 m-6"
    >
      {{ error }}
    </div>

    <div
      v-else-if="!immersiveItems.length"
      class="flex-1 flex items-center justify-center"
    >
      <div class="text-center">
        <span class="i-lucide-eye size-8 text-n-slate-6 mx-auto block mb-2" />
        <p class="text-sm font-medium text-n-slate-9">
          No immersive walkthroughs added yet.
        </p>
      </div>
    </div>

    <template v-else>
      <!-- Header -->
      <div
        class="flex items-center justify-between gap-4 px-6 pt-6 pb-4 shrink-0 flex-wrap"
      >
        <div class="flex items-start gap-3">
          <span
            class="flex items-center justify-center size-10 rounded-xl bg-woot-50 dark:bg-woot-500/10 shrink-0"
          >
            <span
              class="i-lucide-eye size-5 text-woot-600 dark:text-woot-400"
            />
          </span>
          <div>
            <h1 class="text-base font-semibold text-n-slate-12">
              Immersive Walkthrough
            </h1>
            <p class="text-sm text-n-slate-9 mt-0.5">
              Explore {{ project?.name || 'the project' }} in 360°.
            </p>
          </div>
        </div>
        <button
          v-if="fullscreenSupported"
          type="button"
          class="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-n-weak hover:bg-n-alpha-2 transition-colors shrink-0"
          @click="toggleFullscreen"
        >
          <span
            :class="isFullscreen ? 'i-lucide-minimize' : 'i-lucide-maximize'"
            class="size-3.5"
          />
          {{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}
        </button>
      </div>

      <!-- Scene selector -->
      <div
        v-if="immersiveItems.length > 1"
        class="flex flex-wrap gap-2 px-6 pb-4 shrink-0"
      >
        <button
          v-for="doc in immersiveItems"
          :key="doc.id"
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
          :class="
            activeItem?.id === doc.id
              ? 'bg-woot-500 text-white'
              : 'bg-n-alpha-2 text-n-slate-10 hover:bg-n-alpha-3'
          "
          @click="selectItem(doc)"
        >
          <span class="i-lucide-scan size-3" />
          {{ doc.title }}
        </button>
      </div>

      <div
        ref="viewerEl"
        class="flex-1 relative bg-n-alpha-1 mx-6 mb-4 rounded-xl overflow-hidden border border-n-weak"
      >
        <iframe
          v-if="activeItem"
          :key="activeItem.id"
          ref="iframeEl"
          :src="activeItem.shareable_url"
          class="w-full h-full border-0"
          allow="fullscreen; xr-spatial-tracking; gyroscope; accelerometer"
          allowfullscreen
          title="Immersive walkthrough"
          @load="onFrameLoad"
        />

        <!-- In-fullscreen controls (the header button leaves view when
             requestFullscreen targets this element) -->
        <button
          v-if="fullscreenSupported"
          type="button"
          class="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/60 text-white text-xs font-medium hover:bg-black/75 transition-colors"
          @click="toggleFullscreen"
        >
          <span
            :class="isFullscreen ? 'i-lucide-minimize' : 'i-lucide-maximize'"
            class="size-3.5"
          />
          {{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}
        </button>

        <!-- Embedding-blocked fallback -->
        <div
          v-if="suspectBlocked"
          class="absolute inset-0 flex items-center justify-center bg-n-background"
        >
          <div class="text-center max-w-sm px-6">
            <span
              class="i-lucide-shield-alert size-8 text-yellow-500 mx-auto block mb-3"
            />
            <p class="text-sm font-medium text-n-slate-11 mb-1">
              This immersive experience cannot be displayed inside NEXCEO.
            </p>
            <a
              :href="activeItem?.shareable_url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 transition-colors"
            >
              Open in New Tab
              <span class="i-lucide-external-link size-3.5" />
            </a>
          </div>
        </div>
      </div>

      <div class="px-6 pb-4 -mt-2 shrink-0">
        <a
          v-if="activeItem"
          :href="activeItem.shareable_url"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1.5 text-xs text-n-slate-8 hover:text-woot-600 dark:hover:text-woot-400 transition-colors"
        >
          Trouble viewing? Open in a new tab
          <span class="i-lucide-external-link size-3" />
        </a>
      </div>
    </template>
  </div>
</template>
